import { RichTextEditor as MantineRichTextEditor, Link } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useState } from "react";
import { Box, Text, Textarea, Button, Group } from "@mantine/core";
import { FiCode, FiEye } from "react-icons/fi";

interface RichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    label?: string;
    error?: string | null;
    maxWords?: number;
    required?: boolean;
    minHeight?: number;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
    value,
    onChange,
    placeholder = "Enter text...",
    label,
    error,
    maxWords,
    required = false,
    minHeight = 120,
}) => {
    const [isCodeView, setIsCodeView] = useState(false);
    const [htmlCode, setHtmlCode] = useState(value || "");
    useEffect(() => {
        const styleId = "richTextEditorStyles";
        if (!document.getElementById(styleId)) {
            const style = document.createElement("style");
            style.id = styleId;
            style.textContent = `
                /* Placeholder styling for empty editor */
                .ProseMirror p.is-editor-empty:first-child::before {
                    color: #adb5bd;
                    content: attr(data-placeholder);
                    float: left;
                    height: 0;
                    pointer-events: none;
                }

                /* Ensure proper styling when editor is empty */
                .mantine-RichTextEditor-content[data-placeholder]:empty::before {
                    content: attr(data-placeholder);
                    color: #adb5bd;
                    pointer-events: none;
                    position: absolute;
                }

                /* Focus styles */
                .mantine-RichTextEditor-content:focus {
                    outline: none;
                }

                /* Ensure the editor has proper padding and min-height */
                .mantine-RichTextEditor-content {
                    padding: 12px;
                    min-height: 120px;
                }

                /* Make sure the ProseMirror editor is visible */
                .ProseMirror {
                    min-height: inherit;
                    padding: 8px;
                }

                /* Add some spacing for better readability */
                .ProseMirror p {
                    margin-bottom: 0.5em;
                }

                .ProseMirror h1,
                .ProseMirror h2,
                .ProseMirror h3,
                .ProseMirror h4 {
                    margin-top: 1em;
                    margin-bottom: 0.5em;
                    font-weight: 600;
                }

                .ProseMirror ul,
                .ProseMirror ol {
                    padding-left: 1.5em;
                    margin-bottom: 0.5em;
                }
            `;
            document.head.appendChild(style);
        }
        return () => {
            // Cleanup is optional - you can remove this if you want styles to persist
        };
    }, []);

    const editor = useEditor({
        extensions: [StarterKit, Link],
        content: value || "",
        editorProps: {
            attributes: {
                'data-placeholder': placeholder,
            },
        },
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            const text = editor.getText();

            // Check word count if maxWords is specified
            if (maxWords) {
                const words = text.trim().split(/\s+/).filter(w => w.length > 0);
                const wordCount = words.length;

                if (wordCount > maxWords) {
                    // Prevent further updates when limit is reached
                    return;
                }
            }

            setHtmlCode(html);
            onChange(html);
        },
    });

    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value || "");
            setHtmlCode(value || "");
        }
    }, [value, editor]);

    const toggleView = () => {
        if (isCodeView) {
            if (editor) {
                editor.commands.setContent(htmlCode);
                onChange(htmlCode);
            }
        }
        setIsCodeView(!isCodeView);
    };

    const handleCodeChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newHtml = event.currentTarget.value;
        setHtmlCode(newHtml);
        onChange(newHtml);
    };

    const getCurrentWordCount = () => {
        if (!editor) return 0;
        const text = editor.getText();
        const words = text.trim().split(/\s+/).filter(w => w.length > 0);
        return words.length;
    };

    const wordCount = getCurrentWordCount();
    const isAtLimit = maxWords ? wordCount >= maxWords : false;

    return (
        <Box>
            <Group justify="space-between" mb={4}>
                {label && (
                    <Text size="sm" fw={500}>
                        {label}
                        {required && <span style={{ color: "red" }}> *</span>}
                    </Text>
                )}
                <Button
                    size="xs"
                    variant="subtle"
                    leftSection={isCodeView ? <FiEye size={14} /> : <FiCode size={14} />}
                    onClick={toggleView}
                >
                    {isCodeView ? "Visual View" : "Code View"}
                </Button>
            </Group>

            {!isCodeView ? (
                <MantineRichTextEditor
                    editor={editor}
                    style={{
                        minHeight: `${minHeight}px`,
                        border: error ? "1px solid red" : undefined,
                    }}
                >
                    <MantineRichTextEditor.Toolbar sticky stickyOffset={60}>
                        <MantineRichTextEditor.ControlsGroup>
                            <MantineRichTextEditor.Bold />
                            <MantineRichTextEditor.Italic />
                            <MantineRichTextEditor.Underline />
                            <MantineRichTextEditor.Strikethrough />
                            <MantineRichTextEditor.ClearFormatting />
                        </MantineRichTextEditor.ControlsGroup>

                        <MantineRichTextEditor.ControlsGroup>
                            <MantineRichTextEditor.H1 />
                            <MantineRichTextEditor.H2 />
                            <MantineRichTextEditor.H3 />
                            <MantineRichTextEditor.H4 />
                        </MantineRichTextEditor.ControlsGroup>

                        <MantineRichTextEditor.ControlsGroup>
                            <MantineRichTextEditor.Blockquote />
                            <MantineRichTextEditor.Hr />
                            <MantineRichTextEditor.BulletList />
                            <MantineRichTextEditor.OrderedList />
                        </MantineRichTextEditor.ControlsGroup>

                        <MantineRichTextEditor.ControlsGroup>
                            <MantineRichTextEditor.Link />
                            <MantineRichTextEditor.Unlink />
                        </MantineRichTextEditor.ControlsGroup>

                        <MantineRichTextEditor.ControlsGroup>
                            <MantineRichTextEditor.Undo />
                            <MantineRichTextEditor.Redo />
                        </MantineRichTextEditor.ControlsGroup>
                    </MantineRichTextEditor.Toolbar>

                    <MantineRichTextEditor.Content />
                </MantineRichTextEditor>
            ) : (
                <Textarea
                    value={htmlCode}
                    onChange={handleCodeChange}
                    placeholder="HTML Code"
                    minRows={Math.ceil(minHeight / 24)}
                    autosize
                    styles={{
                        input: {
                            fontFamily: "monospace",
                            fontSize: "0.9em",
                            border: error ? "1px solid red" : undefined,
                        },
                    }}
                />
            )}

            {maxWords && (
                <Text size="xs" c={isAtLimit ? "red" : "dimmed"} mt={4}>
                    {wordCount} / {maxWords} words
                    {isAtLimit && " - Maximum reached"}
                </Text>
            )}

            {error && (
                <Text size="xs" c="red" mt={4}>
                    {error}
                </Text>
            )}
        </Box>
    );
};
